<template>
  <nav class="sp-table-page-numbers" part="page-navigation" :aria-label="navigationLabel">
    <TablePageStep :label="firstLabel" :disabled="isFirstPage" @activate="emit('change', 1)" />
    <TablePageStep :label="previousLabel" :disabled="isFirstPage" @activate="emit('change', page - 1)" />

    <template v-for="(entry, index) in pages" :key="`${entry}-${index}`">
      <span v-if="entry === PAGE_GAP" class="sp-table-page-numbers__gap" aria-hidden="true">&hellip;</span>
      <TablePageStep
        v-else
        :label="String(entry)"
        :current="entry === page"
        :accessible-name="pageLabelFor(entry)"
        @activate="emit('change', entry)"
      />
    </template>

    <TablePageStep :label="nextLabel" :disabled="isLastPage" @activate="emit('change', page + 1)" />
    <TablePageStep :label="lastLabel" :disabled="isLastPage" @activate="emit('change', pageCount)" />
  </nav>
</template>

<script setup>
import { computed } from "vue";

import { t, tAccessibleName } from "#ce/shared/components-i18n";
import { buildPageList, PAGE_GAP } from "#ce/components/SpTable/paging.js";
import TablePageStep from "./TablePageStep.ce.vue";

const props = defineProps({
  page: {
    type: Number,
    default: 1,
  },
  pageCount: {
    type: Number,
    default: 1,
  },
  isFirstPage: {
    type: Boolean,
    default: false,
  },
  isLastPage: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["change"]);

const pages = computed(() => buildPageList(props.page, props.pageCount));

const navigationLabel = computed(() => tAccessibleName("table_pagination"));
const firstLabel = computed(() => t("table_first_page"));
const previousLabel = computed(() => t("table_previous_page"));
const nextLabel = computed(() => t("table_next_page"));
const lastLabel = computed(() => t("table_last_page"));

function pageLabelFor(number) {
  return tAccessibleName("table_go_to_page", { page: number });
}
</script>

<style lang="scss" scoped>
.sp-table-page-numbers {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-comp-table-pager-step-gap, var(--sp-ref-scale-2));

  &__gap {
    padding: var(--sp-ref-scale-2) var(--sp-ref-scale-4);
    color: var(--table-header-color);
  }
}
</style>
